import { hasHeaderFormula } from './sheetFormula';

export const CSV_IMPORT_MAX_ROWS = 250;

const TRUE_VALUES = new Set(['true', 'yes', 'y', '1']);

const normalizeCsvKey = (value) => `${value || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');

const isEmptyCsvValue = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'boolean') return value !== true;
  return `${value ?? ''}`.trim() === '';
};

const parseMultiValueCell = (rawValue) => {
  const source = `${rawValue || ''}`.trim();
  if (!source) return [];
  const delimiter = source.includes('|')
    ? '|'
    : source.includes(';')
      ? ';'
      : source.includes('\n')
        ? '\n'
        : ',';

  return Array.from(new Set(
    source
      .split(delimiter)
      .map((item) => item.trim())
      .filter(Boolean)
  ));
};

const coerceCsvValueForHeader = (header, rawValue) => {
  const value = `${rawValue ?? ''}`.trim();
  if (!header) return value;

  if (header.type === 'checkbox') {
    return TRUE_VALUES.has(value.toLowerCase());
  }

  if (header.type === 'multiselect' || header.type === 'list') {
    return parseMultiValueCell(value);
  }

  if (['number', 'rating', 'currency', 'percent'].includes(header.type)) {
    if (!value) return '';
    const normalized = value.replace(/[%,$\s]/g, '').replace(/,/g, '');
    if (!normalized) return '';
    const numeric = Number(normalized);
    return Number.isFinite(numeric) ? numeric : '';
  }

  return value;
};

const getColumnExample = (header) => {
  switch (`${header?.type || ''}`.trim()) {
    case 'checkbox':
      return 'true';
    case 'multiselect':
    case 'list':
      return 'Alpha | Beta';
    case 'number':
      return '42';
    case 'rating':
      return '4';
    case 'currency':
      return '1250.50';
    case 'percent':
      return '87.5';
    case 'email':
      return 'name@example.com';
    case 'telephone':
      return '+27 82 555 0123';
    case 'address':
      return '12 Long Street, Cape Town';
    case 'hyperlink':
    case 'document':
    case 'image':
    case 'youtube':
      return 'https://example.com';
    case 'date':
      return '2026-04-16';
    case 'time':
      return '14:30';
    case 'datetime':
      return '2026-04-16T14:30';
    default:
      return 'Sample text';
  }
};

const getColumnNotes = (header) => {
  const options = Array.isArray(header?.options)
    ? header.options.map((option) => `${option || ''}`.trim()).filter(Boolean)
    : [];

  switch (`${header?.type || ''}`.trim()) {
    case 'checkbox':
      return 'Use true/false, yes/no, or 1/0.';
    case 'multiselect':
    case 'list':
      return 'Separate multiple values with | or ; inside one cell.';
    case 'date':
      return 'Use YYYY-MM-DD.';
    case 'time':
      return 'Use HH:MM.';
    case 'datetime':
      return 'Use YYYY-MM-DDTHH:MM.';
    case 'number':
    case 'rating':
    case 'currency':
    case 'percent':
      return 'Use numeric values only.';
    default:
      if (options.length) {
        return `Options: ${options.join(', ')}`;
      }
      return 'Leave blank to skip this field.';
  }
};

export const getSheetCsvColumns = (headers = []) => (
  (Array.isArray(headers) ? headers : [])
    .filter((header) => {
      const headerId = `${header?.id || ''}`.trim();
      const label = `${header?.label || ''}`.trim();
      return Boolean(
        headerId &&
        label &&
        !header?.systemField &&
        headerId !== 'system_active_actions' &&
        header?.type !== 'profile' &&
        !hasHeaderFormula(header)
      );
    })
    .map((header) => ({
      header,
      headerId: header.id,
      label: `${header.label || ''}`.trim(),
      type: `${header.type || 'text'}`.trim(),
      example: getColumnExample(header),
      notes: getColumnNotes(header),
      aliases: Array.from(new Set([
        `${header.label || ''}`.trim(),
        `${header.id || ''}`.trim()
      ].filter(Boolean)))
    }))
);

const escapeCsvCell = (value) => {
  const stringValue = `${value ?? ''}`;
  if (!/[",\n\r]/.test(stringValue)) return stringValue;
  return `"${stringValue.replace(/"/g, '""')}"`;
};

export const buildSheetCsvTemplate = (headers = []) => {
  const columns = getSheetCsvColumns(headers);
  return `${columns.map((column) => escapeCsvCell(column.label)).join(',')}\n`;
};

export const parseCsvText = (text) => {
  const source = `${text || ''}`;
  const rows = [];
  let currentRow = [];
  let currentCell = '';
  let index = 0;
  let insideQuotes = false;

  while (index < source.length) {
    const char = source[index];
    const nextChar = source[index + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentCell += '"';
        index += 2;
        continue;
      }
      insideQuotes = !insideQuotes;
      index += 1;
      continue;
    }

    if (!insideQuotes && char === ',') {
      currentRow.push(currentCell);
      currentCell = '';
      index += 1;
      continue;
    }

    if (!insideQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && nextChar === '\n') {
        index += 1;
      }
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentRow = [];
      currentCell = '';
      index += 1;
      continue;
    }

    currentCell += char;
    index += 1;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }

  return rows;
};

export const parseSheetCsvImport = (text, headers = []) => {
  const parsedRows = parseCsvText(text).filter((row) => row.some((cell) => `${cell || ''}`.trim() !== ''));
  if (!parsedRows.length) {
    throw new Error('The CSV file is empty.');
  }

  const columns = getSheetCsvColumns(headers);
  if (!columns.length) {
    throw new Error('This sheet has no CSV-importable columns yet.');
  }

  const [headerRow, ...dataRows] = parsedRows;
  const columnLookup = new Map();
  columns.forEach((column) => {
    column.aliases.forEach((alias) => {
      columnLookup.set(normalizeCsvKey(alias), column);
    });
  });

  const usedHeaderIds = new Set();
  const ignoredColumns = [];
  const mappedColumns = headerRow.map((cell, index) => {
    const match = columnLookup.get(normalizeCsvKey(cell));
    if (!match || usedHeaderIds.has(match.headerId)) {
      if (`${cell || ''}`.trim()) {
        ignoredColumns.push(`${cell}`.trim());
      }
      return null;
    }
    usedHeaderIds.add(match.headerId);
    return {
      index,
      column: match
    };
  }).filter(Boolean);

  if (!mappedColumns.length) {
    throw new Error('None of the CSV columns match this sheet. Use the downloaded template to align the column names.');
  }

  if (dataRows.length > CSV_IMPORT_MAX_ROWS) {
    throw new Error(`CSV imports are limited to ${CSV_IMPORT_MAX_ROWS} rows at a time.`);
  }

  let skippedRowCount = 0;
  const rows = dataRows.reduce((acc, row) => {
    const values = {};

    mappedColumns.forEach(({ index, column }) => {
      values[column.headerId] = coerceCsvValueForHeader(column.header, row[index] ?? '');
    });

    const hasValues = mappedColumns.some(({ column }) => !isEmptyCsvValue(values[column.headerId]));
    if (!hasValues) {
      skippedRowCount += 1;
      return acc;
    }

    acc.push({ values });
    return acc;
  }, []);

  return {
    rows,
    ignoredColumns,
    missingColumns: columns.filter((column) => !usedHeaderIds.has(column.headerId)).map((column) => column.label),
    mappedColumnCount: mappedColumns.length,
    skippedRowCount
  };
};
